"use client";

import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import {
    FlexColumn,
    FlexColumnAlignJustifyCenter,
    FlexColumnJustifyCenter,
    FlexRowAlignCenter,
} from "@/components/Flex";
import { AuthEvent } from "@/features/Auth/AuthPopup/utils/constants";
import { useCheckAuth } from "@/hooks/useCheckAuth";
import br from "@/styles/breakpoints.module.css";
import { borderRadius } from "@/utils/borderRadius";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { AI_TOOLS, AI_TOOLS_FORM_INPUTS, AI_TOOLS_SETTINGS_H1_TITLES } from "@knowt/syncing/constants/ai-tools";
import { AICompletionType } from "@knowt/syncing/graphql/schema";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { updateUserInfo } from "@knowt/syncing/hooks/user/utils";
import { useSyncedState } from "@knowt/syncing/utils/hooks/useSyncedState";
import clsx from "clsx";
import { RotateCcw, Sparkle, Star } from "lucide-react";
import toast from "react-hot-toast";
import { useToolSettings } from "../hooks/useToolSettings";
import AiFormInputFactory from "../utils/AiFormInputFactory";
import Image from "next/image";
import Mountable from "@/components/styled/div/Mountable";
import LargeScreenLoading from "@/components/LargeScreenLoading";
import noop from "@knowt/syncing/utils/noop";
import { useSearchParams } from "next/navigation";

const FavButton = ({ handleOnStar, isFavorite }) => {
    return (
        <CircularRectTextButton
            className="bodyBold1"
            onClick={handleOnStar}
            style={{
                height: "5rem",
                gap: spacing.XS,
                color: themeColors.neutralBlack,
                backgroundColor: themeColors.neutralWhite,
                padding: `${spacing.SM} ${spacing.MD}`,
                border: `1px solid ${themeColors.neutral1}`,
            }}>
            <Star
                size={20}
                color={isFavorite ? themeColors.primary3 : themeColors.neutralBlack}
                fill={isFavorite ? themeColors.primary3 : themeColors.neutralWhite}
            />
            Favorite
        </CircularRectTextButton>
    );
};

const ToolSettings = ({ tool }: { tool: AICompletionType }) => {
    const toolDetails = AI_TOOLS[tool];
    const { user } = useCurrentUser();
    const searchParams = useSearchParams();
    const folderId = searchParams.get("folderId");
    const classId = searchParams.get("classId");

    const favoriteTools = user?.favTools ?? [];
    const [isFavorited, setIsFavorited] = useSyncedState(favoriteTools.includes(tool));
    const { checkAuth } = useCheckAuth();

    const {
        settingsValues,
        droppedFile,
        setDroppedFile,
        handleOnChange,
        handleOnClear,
        handleOnGenerate,
        handleHelpFillField,
        isUploadingFile,
        handleStopUploadFile,
        isGeneratingNote,
        isPollingForTranscript,
        isShowingMissingInputs,
    } = useToolSettings({ tool: AICompletionType[tool], folderId, classId });

    const updateUserFavorites = async (favTools: string[]) => {
        try {
            setIsFavorited(prev => !prev);

            await updateUserInfo(
                {
                    favTools: favTools.length ? favTools : null,
                },
                true
            );
        } catch {
            toast.error("Something went wrong, please try again later.");
        }
    };

    const handleOnStar = async () => {
        const isAuthenticated = await checkAuth({
            event: AuthEvent.FAVORITE_AI_TOOL,
        });

        if (!isAuthenticated) return;

        if (isFavorited) {
            updateUserFavorites(favoriteTools.filter(toolName => toolName !== tool));
            toast.success("Removed from favorites");
            return;
        }

        updateUserFavorites([...favoriteTools, tool]);
        toast.success("Added to favorites");
    };

    if (!AI_TOOLS_FORM_INPUTS[tool]) return; //to remove

    return (
        <FlexColumnJustifyCenter
            style={{ flex: 1, gap: spacing.LG, position: "relative", alignItems: "center", maxWidth: "78rem" }}>
            <FlexRowAlignCenter
                style={{
                    backgroundColor: themeColors.neutralWhite,
                    borderRadius: borderRadius.card,
                    padding: `${spacing.MD}`,
                    justifyContent: "space-between",
                    color: themeColors.pureBlack,
                    gap: spacing.XL,
                }}>
                <FlexRowAlignCenter style={{ gap: spacing.SM }}>
                    <FlexColumnAlignJustifyCenter
                        style={{ padding: spacing.XS_2, borderRadius: "0.9rem", backgroundColor: toolDetails.color }}>
                        <Image
                            src={`/images/aiTools/${tool}.svg`}
                            alt={tool}
                            width={33}
                            height={33}
                            style={{ objectFit: "contain" }}
                        />
                    </FlexColumnAlignJustifyCenter>
                    <FlexColumn style={{ gap: "0.5rem", flexWrap: "wrap" }}>
                        <h1 className={"bodyBold1"} style={{ color: themeColors.neutralBlack }}>
                            {AI_TOOLS_SETTINGS_H1_TITLES[tool]}
                        </h1>
                        <p className={clsx(["body2", br.smDownDisplayNone])} style={{ color: themeColors.neutral3 }}>
                            {toolDetails?.description}
                        </p>
                    </FlexColumn>
                </FlexRowAlignCenter>
                <FavButton isFavorite={isFavorited} handleOnStar={handleOnStar} />
            </FlexRowAlignCenter>
            <FlexColumnAlignJustifyCenter
                style={{
                    width: "100%",
                    gap: spacing.MD,
                    alignSelf: "center",
                    padding: `${spacing.MD}`,
                    borderRadius: borderRadius.card,
                    backgroundColor: themeColors.neutralWhite,
                }}>
                <FlexRowAlignCenter style={{ width: "100%", justifyContent: "space-between" }}>
                    <CircularRectTextButton
                        onClick={handleOnClear}
                        style={{
                            gap: spacing.XS,
                            color: themeColors.errorPrimary,
                            padding: `${spacing.XS_2} ${spacing.MD}`,
                            border: `1px solid ${themeColors.neutral1}`,
                        }}>
                        <RotateCcw size={20} color={themeColors.errorPrimary} />
                        Clear
                    </CircularRectTextButton>
                    <CircularRectTextButton
                        id={`${tool}-generate`}
                        onClick={handleOnGenerate}
                        style={{
                            gap: spacing.XS,
                            color: themeColors.pureBlack,
                            background: themeColors.aiGradient,
                            padding: `${spacing.XS_2} ${spacing.MD}`,
                            border: `1px solid ${themeColors.ai}`,
                            opacity: isUploadingFile || isPollingForTranscript ? 0.5 : 1,
                        }}>
                        <Sparkle size={20} color={themeColors.ai} fill={themeColors.ai} />
                        {isPollingForTranscript ? "Proccessing your file" : "Generate"}
                    </CircularRectTextButton>
                </FlexRowAlignCenter>

                <FlexColumn style={{ gap: spacing.LG, width: "100%" }}>
                    <AiFormInputFactory
                        tool={tool}
                        inputs={AI_TOOLS_FORM_INPUTS[tool]}
                        values={settingsValues}
                        onChange={handleOnChange}
                        droppedFile={droppedFile}
                        setDroppedFile={setDroppedFile}
                        handleHelpFillField={handleHelpFillField}
                        isUploadingFile={isUploadingFile}
                        handleStopUloadFile={handleStopUploadFile}
                        isShowingMissingInputs={isShowingMissingInputs}
                    />
                </FlexColumn>
            </FlexColumnAlignJustifyCenter>
            <Mountable mount={isGeneratingNote}>
                <LargeScreenLoading isOpen={isGeneratingNote} onClose={noop} />
            </Mountable>
        </FlexColumnJustifyCenter>
    );
};

export default ToolSettings;
